import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Handshake, ArrowRightLeft, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Owner-side: pick a partner company to share the job with. Partner-side: assign one of our servers. */
export function SharePanel({ job, user, onChanged }) {
  const isAdmin = user?.role === "admin";
  const [partners, setPartners] = useState([]);
  const [servers, setServers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    if (job.shared_in) api.get("/servers").then((r) => setServers(r.data.filter((s) => s.active !== false))).catch(() => {});
    else api.get("/partners").then((r) => setPartners(r.data.filter((p) => p.status === "active"))).catch(() => {});
  }, [isAdmin, job.shared_in]);

  if (job.shared_in) {
    return (
      <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 space-y-2" data-testid="shared-in-panel">
        <div className="flex items-center gap-2 text-sm font-medium text-violet-900"><Handshake className="h-4 w-4" />Shared by {job.shared_from_org_name}</div>
        <div className="text-xs text-violet-800">You can log attempts, add notes and print the field sheet. Client details, affidavits and job edits stay with {job.shared_from_org_name}.</div>
        {isAdmin && (
          <div className="space-y-2">
            <div className="sm-label mb-1">Your server on this job</div>
            <Select value={job.server_id_partner || "none"} onValueChange={async (v) => {
              setBusy(true);
              try { const { data } = await api.post(`/jobs/${job.id}/shared-server`, { server_id: v === "none" ? null : v }); toast.success(data.server_name ? (data.emailed ? `Assigned to ${data.server_name} — job emailed to ${data.server_email}` : `Assigned to ${data.server_name}`) : "Unassigned"); onChanged(); }
              catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
            }} disabled={busy}>
              <SelectTrigger className="h-9 bg-white" data-testid="shared-server-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Unassigned</SelectItem>{servers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.email ? "" : " (no email)"}</SelectItem>)}</SelectContent>
            </Select>
            {job.server_id_partner && (
              <div className={`text-xs font-medium ${job.server_acks?.[job.server_id_partner] ? "text-emerald-700" : "text-amber-700"}`} data-testid="shared-server-ack-status">{job.server_acks?.[job.server_id_partner] ? `✓ ${job.shared_server_name} acknowledged ${new Date(job.server_acks[job.server_id_partner].at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : "Not yet acknowledged"}</div>
            )}
            {job.server_id_partner && (
              <Button size="sm" variant="outline" className="w-full h-8 bg-white" disabled={busy} data-testid="shared-notify-server-button" onClick={async () => {
                setBusy(true);
                try { const { data } = await api.post(`/jobs/${job.id}/notify-server`); toast.success(`Job emailed to ${data.to}`); onChanged(); }
                catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); } finally { setBusy(false); }
              }}><Mail className="h-3.5 w-3.5 mr-1.5" />Email job to {job.shared_server_name || "server"}{job.documents?.length ? ` (${job.documents.length} doc${job.documents.length > 1 ? "s" : ""})` : ""}</Button>
            )}
            <div className="text-[11px] text-violet-800">Assigning emails your server the field sheet and all uploaded service documents. Use the button to resend.</div>
          </div>
        )}
      </div>
    );
  }
  if (!isAdmin && !job.shared_with_org_id) return null;
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${job.shared_with_org_id ? "border-violet-200 bg-violet-50" : "border-slate-200 bg-white"}`} data-testid="share-panel">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><ArrowRightLeft className="h-4 w-4 text-violet-700" />Partner collaboration</div>
      {job.shared_with_org_id ? (
        <div className="text-xs text-violet-900" data-testid="shared-with-text">Shared with <b>{job.shared_with_org_name}</b>{job.shared_server_name ? ` · their server: ${job.shared_server_name}` : " · not yet assigned on their side"}. Their attempts appear here in real time.</div>
      ) : <div className="text-xs text-slate-500">Send this job to a partner company so their servers can make attempts. Your client stays hidden from them.</div>}
      {isAdmin && (partners.length || job.shared_with_org_id ? (
        <div className="flex gap-2">
          <Select value={job.shared_with_org_id || "none"} onValueChange={async (v) => {
            if (v === "none" && !window.confirm(`Stop sharing with ${job.shared_with_org_name}? Their attempts stay on the job.`)) return;
            setBusy(true);
            try { await api.post(`/jobs/${job.id}/share`, { partner_org_id: v === "none" ? null : v }); toast.success(v === "none" ? "Sharing stopped" : "Job shared"); onChanged(); }
            catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
          }} disabled={busy}>
            <SelectTrigger className="h-9 bg-white" data-testid="share-partner-select"><SelectValue placeholder="Share with…" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Not shared</SelectItem>{partners.map((p) => <SelectItem key={p.partner_org_id} value={p.partner_org_id}>{p.partner_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ) : <div className="text-xs text-slate-500">No partner companies yet — add one under <a href="/settings" className="sm-link">Settings → Partner companies</a>.</div>)}
      {isAdmin && !job.shared_with_org_id && partners.length === 0 && null}
    </div>
  );
}

export function PartnersCard() {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/partners").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const invite = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/partners", { email }); toast.success("Invite sent — they'll see it under Settings → Partner companies"); setEmail(""); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="partners-card">
      <div className="sm-label flex items-center gap-1"><Handshake className="h-3.5 w-3.5" /> Partner companies</div>
      <div className="text-xs text-slate-500">Partners can receive jobs you share and log attempts on them (and share theirs with you). They never see your clients.</div>
      <form onSubmit={invite} className="flex gap-2">
        <input className="h-9 flex-1 rounded-md border border-slate-200 px-3 text-sm" placeholder="Other company's admin email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="partner-email" />
        <Button type="submit" size="sm" className="sm-green h-9" disabled={busy || !email.includes("@")} data-testid="partner-invite">Invite</Button>
      </form>
      {list.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2 text-sm" data-testid={`partner-${p.id}`}>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{p.partner_name}</div>
            <div className="text-xs text-slate-500">{p.status === "active" ? "Active partner" : p.incoming ? `Invited you (${p.requester_name})` : `Invite pending — waiting on ${p.target_email}`}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            {p.incoming && <Button size="sm" className="sm-green h-8" data-testid={`partner-accept-${p.id}`} onClick={async () => { await api.post(`/partners/${p.id}/accept`); toast.success("Partnership active"); load(); }}>Accept</Button>}
            <button type="button" className="text-xs text-rose-600 hover:underline" data-testid={`partner-remove-${p.id}`} onClick={async () => { if (!window.confirm("Remove this partner? Any shared jobs will be un-shared.")) return; await api.delete(`/partners/${p.id}`); load(); }}>{p.status === "active" ? "Remove" : p.incoming ? "Decline" : "Cancel"}</button>
          </div>
        </div>
      ))}
    </div>
  );
}
